import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

function SetupAccount() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [error, setError] = useState('');
  const [tokenError, setTokenError] = useState('');
  const [success, setSuccess] = useState(false);
  const [accountInfo, setAccountInfo] = useState(null);

  const token = searchParams.get('token');

  // Validate token on mount
  useEffect(() => {
    if (!token) {
      setTokenError('No setup token provided. Please use the link from your email.');
      setValidating(false);
      return;
    }

    const validateToken = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/public/validate-setup-token?token=${token}`);
        const data = await response.json();

        if (!response.ok) {
          setTokenError(data.error || 'Invalid or expired setup link.');
        } else {
          setAccountInfo(data);
        }
      } catch (err) {
        setTokenError('Failed to validate setup link. Please try again.');
      } finally {
        setValidating(false);
      }
    };

    validateToken();
  }, [token]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const validatePassword = (password) => {
    const errors = [];
    if (password.length < 8) errors.push('At least 8 characters');
    if (!/[A-Z]/.test(password)) errors.push('One uppercase letter');
    if (!/[a-z]/.test(password)) errors.push('One lowercase letter');
    if (!/[0-9]/.test(password)) errors.push('One number');
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password strength
    const pwdErrors = validatePassword(formData.password);
    if (pwdErrors.length > 0) {
      setError('Password must include: ' + pwdErrors.join(', '));
      return;
    }

    setLoading(true);

    try {
      // Step 1: Set the password
      const response = await fetch(`${process.env.REACT_APP_API_URL}/public/setup-account`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          password: formData.password
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to set password');
      }

      // Step 2: Auto-login with the new credentials
      const loginResponse = await fetch(`${process.env.REACT_APP_API_URL}/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: accountInfo?.email,
          password: formData.password,
          tenantId: accountInfo?.tenantId
        })
      });

      const loginData = await loginResponse.json();

      if (loginResponse.ok && loginData.token) {
        // Store auth data and redirect to tier selection for new signups
        localStorage.setItem('adminToken', loginData.token);
        localStorage.setItem('adminUser', JSON.stringify(loginData.admin));
        // New signups go to tier selection, existing users to dashboard
        navigate('/signup/select-tier');
      } else {
        // Password set but auto-login failed - show success with login button
        setSuccess(true);
      }
    } catch (err) {
      setError(err.message || 'Failed to complete setup. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Loading state while validating token
  if (validating) {
    return (
      <div className="admin-login-container">
        <div className="admin-login-box">
          <div className="admin-access-badge">
            Validating...
          </div>
          <h2 className="admin-login-title">Setting Up Your Account</h2>
          <p className="admin-login-subtitle">
            Please wait while we verify your setup link...
          </p>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="admin-login-container">
        <div className="admin-login-box">
          <div className="admin-access-badge" style={{ background: 'var(--color-success)', color: 'white' }}>
            ✓ ACCOUNT READY
          </div>
          <h2 className="admin-login-title">Setup Complete!</h2>
          <p className="admin-login-subtitle">
            Your password has been set. You can now log in to your admin dashboard.
          </p>
          {accountInfo?.subdomain && (
            <p style={{ fontSize: '14px', color: 'var(--color-text-light)', margin: '15px 0' }}>
              Your site: <strong>{accountInfo.subdomain}.deedstack.com</strong>
            </p>
          )}
          <button
            onClick={() => navigate('/admin/login')}
            className="admin-login-submit"
            style={{ marginTop: '20px' }}
          >
            Go to Admin Login
          </button>
        </div>
      </div>
    );
  }

  // Token error state
  if (tokenError) {
    return (
      <div className="admin-login-container">
        <div className="admin-login-box">
          <div className="admin-access-badge" style={{ background: 'var(--color-error)', color: 'white' }}>
            ! LINK EXPIRED
          </div>
          <h2 className="admin-login-title">Setup Link Invalid</h2>
          <p className="admin-login-subtitle">
            {tokenError}
          </p>
          <p style={{ fontSize: '14px', color: 'var(--color-text-light)', margin: '20px 0' }}>
            Setup links expire after 24 hours. If your link has expired,{' '}
            <a href="https://deedstack.com/contact">contact us</a> to request a new one.
          </p>
        </div>
      </div>
    );
  }

  // Main form
  return (
    <div className="admin-login-container">
      <div className="admin-login-box">
        <div className="admin-access-badge">
          🔐 COMPLETE SETUP
        </div>

        <h2 className="admin-login-title">
          Set Your Password
        </h2>

        {accountInfo?.companyName && (
          <p style={{ fontSize: '14px', color: 'var(--color-brand-text)', marginBottom: '5px', fontWeight: '600' }}>
            {accountInfo.companyName}
          </p>
        )}

        <p className="admin-login-subtitle">
          Create a secure password for your admin account.
        </p>

        {error && (
          <div className="admin-login-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="admin-form-group">
            <label className="admin-form-label">
              Password
            </label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              minLength={8}
              className="admin-form-input"
              placeholder="Create a strong password"
              disabled={loading}
            />
            <div style={{ marginTop: '8px' }}>
              <span style={{
                fontSize: '12px',
                color: formData.password.length >= 8 ? 'var(--color-success)' : '#999',
                marginRight: '10px'
              }}>
                {formData.password.length >= 8 ? '✓' : '○'} 8+ chars
              </span>
              <span style={{
                fontSize: '12px',
                color: /[A-Z]/.test(formData.password) ? 'var(--color-success)' : '#999',
                marginRight: '10px'
              }}>
                {/[A-Z]/.test(formData.password) ? '✓' : '○'} Uppercase
              </span>
              <span style={{
                fontSize: '12px',
                color: /[a-z]/.test(formData.password) ? 'var(--color-success)' : '#999',
                marginRight: '10px'
              }}>
                {/[a-z]/.test(formData.password) ? '✓' : '○'} Lowercase
              </span>
              <span style={{
                fontSize: '12px',
                color: /[0-9]/.test(formData.password) ? 'var(--color-success)' : '#999'
              }}>
                {/[0-9]/.test(formData.password) ? '✓' : '○'} Number
              </span>
            </div>
          </div>

          <div className="admin-form-group-last">
            <label className="admin-form-label">
              Confirm Password
            </label>
            <input
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              className="admin-form-input"
              placeholder="Re-enter your password"
              disabled={loading}
            />
            {formData.confirmPassword && formData.password !== formData.confirmPassword && (
              <p style={{ fontSize: '12px', color: 'var(--color-error)', margin: '5px 0 0 0' }}>
                Passwords do not match
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={loading || formData.password !== formData.confirmPassword}
            className="admin-login-submit"
          >
            {loading ? 'Setting Up...' : 'Complete Setup'}
          </button>
        </form>

        <div className="admin-login-footer" style={{ marginTop: '20px', textAlign: 'center' }}>
          <p style={{ fontSize: '13px', color: 'var(--color-text-light)' }}>
            Need help? <a href="https://deedstack.com/contact">Contact us</a>
          </p>
        </div>
      </div>
    </div>
  );
}

export default SetupAccount;
